import { useMemo } from "react";
import { notesListParams } from "@/features/notes/lib/list-query-params";
import type {
	NotesAdapter,
	NotesCreateVars,
	NotesDeleteVars,
	NotesUpdateVars,
} from "@/features/notes/types";
import {
	useCreateTicketNote,
	useDeleteTicketNote,
	useUpdateTicketNote,
} from "@/services/ticket/ticket.mutations";
import { ticketQueryOptions } from "@/services/ticket/ticket.options";

/**
 * Pure list query options for ticket notes (route loaders).
 */
export function createTicketNotesListQueryOptions(args: {
	ticketId: string;
	organizationId: string;
	start: number;
	count: number;
}) {
	return ticketQueryOptions.notes.list(
		args.ticketId,
		args.start,
		args.count,
		notesListParams(args.organizationId),
		{ organizationId: args.organizationId },
	);
}

/**
 * Ticket-scoped notes adapter (hooks + query option factories).
 */
export function useTicketNotesAdapter(ticketId: string): NotesAdapter {
	const createNote = useCreateTicketNote();
	const updateNote = useUpdateTicketNote();
	const deleteNote = useDeleteTicketNote();

	return useMemo(
		(): NotesAdapter => ({
			kind: "ticket",
			entityId: ticketId,
			listQueryOptions: ({ organizationId, start, count }) =>
				createTicketNotesListQueryOptions({
					ticketId,
					organizationId,
					start,
					count,
				}),
			createMutation: {
				mutateAsync: (vars: NotesCreateVars) =>
					createNote.mutateAsync({
						ticketId: vars.entityId,
						text: vars.text,
						organizationId: vars.organizationId,
					}),
				reset: () => createNote.reset(),
				isPending: createNote.isPending,
				error: (createNote.error as Error | null) ?? null,
			},
			updateMutation: {
				mutateAsync: (vars: NotesUpdateVars) =>
					updateNote.mutateAsync({
						ticketId: vars.entityId,
						noteId: vars.noteId,
						text: vars.text,
						organizationId: vars.organizationId,
					}),
				reset: () => updateNote.reset(),
				isPending: updateNote.isPending,
				error: (updateNote.error as Error | null) ?? null,
			},
			deleteMutation: {
				mutateAsync: (vars: NotesDeleteVars) =>
					deleteNote.mutateAsync({
						ticketId: vars.entityId,
						noteId: vars.noteId,
						organizationId: vars.organizationId,
					}),
				reset: () => deleteNote.reset(),
				isPending: deleteNote.isPending,
				error: (deleteNote.error as Error | null) ?? null,
			},
		}),
		[createNote, deleteNote, ticketId, updateNote],
	);
}
